import { Form, Input, Modal, notification } from 'antd';
import { OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { IOpTableProps, OpTable } from 'components/customAntd/DLS/OpTable/OpTable';
import { OpTableRawColumnType } from 'components/customAntd/DLS/OpTableCore/OpTableCore';
import { TABLE_HEIGHT } from 'constants/ui';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { logUserActivity } from 'store/slices/userActivitySlice';
import { createVisitorWorkflow, deleteVisitorWorkflow, describeVisitorWorkflow, describeVisitorWorkflowConfig, describeVisitorWorkflowFields, describeVisitorWorkflowLocations, fetchVisitorWorkflows } from 'store/slices/visitorWorkflowsSlice';
import { AppDispatch, RootState } from 'store/store';
import { ApiResponse } from 'types/apiTypes';
import { Location } from 'types/locationTypes';
import { CreateVisitorWorkflowPayload, VisitorWorkflow } from 'types/visitorWorkflowTypes';
import { hasPermission } from 'utils/utils';

interface AttendantSignOutTableProps {
    setDrawerVisible: (visible: boolean) => void;
}

const AttendantSignOutTable: React.FC<AttendantSignOutTableProps> = ({ setDrawerVisible }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const { visitorWorkflows, fetchVisitorWorkflowsLoading } = useSelector((state: RootState) => state.visitorWorkflows);

    const [createForm] = OpForm.useForm();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [signOutWorkflows, setSignOutWorkflows] = useState<VisitorWorkflow[]>([]);

    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const hasSignoutconfWrite = hasPermission(tokenScopeList, orgId, 'o', 'signoutconf:w');

    const { t } = useTranslation();
    useEffect(() => {
        if (orgId) {
            dispatch(fetchVisitorWorkflows({ orgId }));
        }
    }, [dispatch, orgId]);


    // Filter and get list of active workflows
    useEffect(() => {
        if (visitorWorkflows && visitorWorkflows.data) {
            const filteredWorkflows = visitorWorkflows.data.filter(visitorWorkflow => visitorWorkflow.workflowTypeId === 2);
            setSignOutWorkflows(filteredWorkflows);
        }
    }, [visitorWorkflows]);

    // Handle deleting a Visitor workflow
    const handleDeleteVisitorWorkflow = async (record: VisitorWorkflow) => {
        try {
            const response = await dispatch(describeVisitorWorkflowLocations({ orgId, visitorWorkflowId: record.id }));
            const locations = response.payload as ApiResponse<Location>;
            if (locations.data.length > 0) {
                notification.error({
                    message: t('notification.error'),
                    description: t('workflow.notification.deleteBlockedError'),
                    placement: 'bottomRight',
                });
            } else {
                await dispatch(deleteVisitorWorkflow({ orgId, visitorWorkflowId: record.id }));
                await dispatch(logUserActivity({ orgId, userActivity: { userId: globalUserId!, activityId: "80", details: record.name || '' } }))
                notification.success({
                    message: t('notification.success'),
                    description: t('workflow.notification.deleteSuccess'),
                    placement: 'bottomRight',
                });
            }
        } catch (error) {
            notification.error({
                message: t('notification.error'),
                description: t('workflow.notification.deleteError'),
                placement: 'bottomRight',
            });
        }
    };

    const handleCreateVisitorWorkflow = async (values: any) => {
        try {
            const payload: CreateVisitorWorkflowPayload = {
                name: values.name,
                workflowTypeId: 2
            };
            await dispatch(createVisitorWorkflow({ orgId, visitorWorkflow: payload }));
            await dispatch(logUserActivity({ orgId, userActivity: { userId: globalUserId!, activityId: "70", details: values.name || '' } }))
            notification.success({
                message: t('notification.success'),
                description: t('workflow.notification.createSuccess'),
                placement: 'bottomRight',
            });
            setIsModalOpen(false);
            createForm.resetFields();
        } catch (error) {
            notification.error({
                message: t('notification.error'),
                description: t('workflow.notification.createError'),
                placement: 'bottomRight',
            });
        }
    };

    const handleAddWorkflow = () => {
        setIsModalOpen(true);
    };

    const columns: OpTableRawColumnType[] = [
        {
            dataIndex: 'name',
            label: t('workflow.attendantSignOut.table.name'),
            filter: {
                type: 'input',
            },
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''), // Alphabetical sorting with null check
        },
    ];

    const opTableProps: IOpTableProps = {
        dataSource: signOutWorkflows,
        columns: columns,
        rowActions: {
            onEditClick: (visitorWorkflow: VisitorWorkflow) => {
                dispatch(describeVisitorWorkflow({ orgId, visitorWorkflowId: visitorWorkflow.id }));
                dispatch(describeVisitorWorkflowLocations({ orgId, visitorWorkflowId: visitorWorkflow.id }));
                dispatch(describeVisitorWorkflowConfig({ orgId, visitorWorkflowId: visitorWorkflow.id }));
                dispatch(describeVisitorWorkflowFields({ orgId, visitorWorkflowId: visitorWorkflow.id }));
                setDrawerVisible(true);
            },
            onDeleteClick: hasSignoutconfWrite ? handleDeleteVisitorWorkflow : undefined,
            deleteModalTitle: () => t('confirmDelete.title', { title: t('workflow.attendantSignOut.itemName') }),
            deleteModalContent: ({ record }) => t('confirmDelete.message', { name: record.name }),
        },
        height: TABLE_HEIGHT,
        allowGlobalSearch: true,
        writeAccess: hasSignoutconfWrite,
        allowAddition: hasSignoutconfWrite ? {
            itemName: t('workflow.attendantSignOut.itemName'),
            onClick: handleAddWorkflow,
        } : false,
        loading: fetchVisitorWorkflowsLoading,
        allowExport: true,
        allowShowHideColumns: true,
    };

    return (
        <>
            <OpTable {...opTableProps} />
            <Modal
                title={t('workflow.attendantSignOut.createWorkflowModal.title')}
                open={isModalOpen}
                onOk={createForm.submit}
                onCancel={() => setIsModalOpen(false)}
                okText={t('workflow.attendantSignOut.createWorkflowModal.ok')}
                cancelText={t('workflow.attendantSignOut.createWorkflowModal.cancel')}
                centered
            >
                <Form form={createForm} onFinish={handleCreateVisitorWorkflow}>
                    <Form.Item
                        label={t('workflow.attendantSignOut.createWorkflowModal.name')}
                        name="name"
                        rules={[{ required: true, message: t('workflow.attendantSignOut.createWorkflowModal.nameRequired') }]}
                    >
                        <Input placeholder={t('workflow.attendantSignOut.createWorkflowModal.namePlaceholder')} />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}

export default AttendantSignOutTable;
